import React from 'react';
import { connect } from 'react-redux';
import { withRouter, Switch, Redirect, Route } from 'react-router-dom';
import _ from 'lodash';
import { Translate } from 'core/translate';
import { withModelInfo } from '../common/ModelInfoContext';
import { computeSpecs } from './vehicleSpecs';
import { computePricing } from '../common/pricing/pricing';
import { normalize as cgiNormalize } from '../common/cgi/cgi';
import {
  toggleConvertibleTop,
  togglePrimaryImageState,
  updateExteriorIndex,
  updateInteriorIndex,
  breakpointChanged,
  updatePostalCode
} from '../store/actions';
import {
  BODY_STYLE_TITLE,
  MOBILE_URL,
  ORIGIN_URL,
  setByModelAndKey
} from '../resources/localStorage/localStorage.service';
import {
  setConfig,
  setNetPrice
} from '../factories/analytics/analytics.service';
import dismissLoadingScreen from '../common/dismissLoadingScreen';
import { hasBeenPreviouslyRead, updateExpiration } from './instructions';
import BreakpointChange from '../common/mediaQuery/BreakpointChange';
import ErrorBoundary from '../common/ErrorBoundary';
import LayoutManager, { LayoutContext } from './LayoutManager';
import ProgressTracker from './ProgressTracker';
import GlobalNav from './globalNav/GlobalNavContainer';
import ProgressNav from './progressNav/ProgressNav';
import PriceSummary from './priceSummary/PriceSummary';
import AccessoriesPrice from './accessoriesPrice/AccessoriesPrice';
import CgiSection from './CgiSection';
import NavNextButton from './NavNextButton';
import Footer from './footer/Footer';
import OptionConflict from './optionConflict/OptionConflictContainer';
import BackToTop from './backToTop/BackToTop';
import InstructionsModal from './instructions/InstructionsModal';
import OptionsLoadingOverlay from './OptionsLoadingOverlay';
import CookiesNotifications from './CookiesNotifications';
import PostalCodeModal from './postalCodeModal/PostalCodeModal';
import StepNav from './stepNav/StepNavContainer';
import { getDeepUrl } from './urlBuilder';
import ClearQueryOnConfigChange from './ClearQueryOnConfigChange';
import AdChoices from './AdChoices';
import ImageZoomModal from './ImageZoomModal';
import Config from '../config/Config';
import Trims from '../config/Trims';
import Colors from '../colors/Colors';
import Packages from '../packages/Packages';
import Exterior from '../exterior/Exterior';
import Interior from '../interior/Interior';
import Accessories from '../accessories/Accessories';
import Summary from '../summary/SummaryContainer';
import { getVisitedTabs } from '../factories/progressTracker/progressTracker';
import computeSteps from './computeSteps';

const mapStateToProps = (state, ownProps) => {
  const {
    appResources,
    config,
    params,
    incentives,
    deviceType,
    isUpdating,
    isDefaultPostalCode,
    viewInventoryUrlTemplate,
    cgi,
    api,
    breakpoint,
    isInteriorImagePrimary,
    urls,
    mobileByoUrl
  } = state;
  const attributes = computeSpecs(state);
  const pricing = computePricing(
    config.msrp,
    incentives,
    config.selections,
    appResources.country,
    appResources.brandBase
  );
  const images = cgiNormalize(
    appResources,
    config,
    cgi.isConvertibleTopDown,
    state.breakpoint
  );

  return {
    appResources,
    config,
    params,
    isMobile: deviceType === 'MOBILE',
    isUpdating,
    isInteriorImagePrimary,
    isDefaultPostalCode,
    pricing,
    attributes,
    viewInventoryUrlTemplate,
    images,
    uiPath: api.uiPath,
    breakpoint,
    cgi,
    urls,
    mobileByoUrl,
    ...ownProps
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onToggleConvertibleTop: () => dispatch(toggleConvertibleTop()),
    onToggleInterior: () => dispatch(togglePrimaryImageState()),
    onChangeExteriorIndex: index => dispatch(updateExteriorIndex(index)),
    onChangeInteriorIndex: index => dispatch(updateInteriorIndex(index)),
    onBreakpointChange: breakpoint => dispatch(breakpointChanged(breakpoint)),
    onPostalCodeUpdate: (postalCode, data, isSummary, cb) => {
      dispatch(updatePostalCode(postalCode, data, isSummary, cb));
    }
  };
};

class Main extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isInstructionsOpen: !hasBeenPreviouslyRead(new Date()),
      isPostalCodeModalOpen: props.isDefaultPostalCode
    };
  }

  componentDidMount() {
    const { appResources, mobileByoUrl, modelInfo } = this.props;

    dismissLoadingScreen();

    setByModelAndKey(appResources, MOBILE_URL, mobileByoUrl);
    setByModelAndKey(appResources, BODY_STYLE_TITLE, modelInfo.title);
  }

  componentDidUpdate() {
    const { config, params, pricing, appResources } = this.props;

    setConfig(params, config);
    setNetPrice(pricing.netPrice);
    setByModelAndKey(
      appResources,
      ORIGIN_URL,
      getDeepUrl(config, params, window.document)
    );
  }

  render() {
    const {
      modelInfo,
      shoppingLinks,
      config,
      params,
      breakpoint,
      pricing,
      images,
      attributes,
      cgi,
      isInteriorImagePrimary,
      appResources,
      urls,
      isUpdating,
      isDefaultPostalCode,
      trimFeatures,
      location,
      onToggleConvertibleTop,
      onToggleInterior,
      onChangeExteriorIndex,
      onChangeInteriorIndex,
      onBreakpointChange,
      onPostalCodeUpdate
    } = this.props;
    const { isInstructionsOpen, isPostalCodeModalOpen } = this.state;

    const { interiorIndex, exteriorIndex, isConvertibleTopDown } = cgi;

    const openPostalCodeModal = () => {
      this.setState({ isPostalCodeModalOpen: true });
    };

    const closePostalCodeModal = () => {
      this.setState({ isPostalCodeModalOpen: false });
    };

    const visited = getVisitedTabs();
    const steps = computeSteps(modelInfo.steps, visited, config, location);

    return (
      <React.Fragment>
        <Route>
          {({ location }) => (
            <React.Fragment>
              <ProgressTracker steps={steps} location={location} />
            </React.Fragment>
          )}
        </Route>
        <ClearQueryOnConfigChange config={config} />
        <LayoutManager>
          <LayoutContext.Consumer>
            {({ navRef, height }) => (
              <React.Fragment>
                <div id="navGroup" ref={navRef}>
                  <ErrorBoundary>
                    <div className="global-nav-container">
                      <GlobalNav
                        params={params}
                        style={config.style}
                        shoppingLinks={shoppingLinks}
                        breakpoint={breakpoint}
                        onEditLocation={openPostalCodeModal}
                      />
                    </div>
                  </ErrorBoundary>
                  <ErrorBoundary>
                    <div id="nav-wrapper" className="row contain-to-grid">
                      <div className="progress-nav-container show-for-large">
                        <ProgressNav steps={steps.items} next={steps.next} />
                      </div>
                      <div className="price-summary-container">
                        <Route>
                          {({ location }) => (
                            <PriceSummary
                              location={location}
                              config={config}
                              pricing={pricing}
                              images={images}
                              interiorIndex={interiorIndex}
                              exteriorIndex={exteriorIndex}
                              switchThumbnail={isInteriorImagePrimary}
                              attributes={attributes}
                              onToggleExpanded={this.onToggleExpanded}
                            />
                          )}
                        </Route>
                      </div>
                    </div>
                  </ErrorBoundary>
                </div>
                <div
                  id="contentView"
                  className="row"
                  style={{ marginTop: height }}>
                  <div className="hide-for-large trim-attributes__mobile">
                    <Translate
                      value="component.trim.attributes.label"
                      context={{
                        driveType: _.get(config, 'driveType.id', ''),
                        config: _.get(config, 'bodyType.formattedConfig', ''),
                        trim: _.get(config, 'style.trimName'),
                        year: modelInfo.year
                      }}
                    />
                  </div>
                  <div id="byo-cgi-container-wrapper" className="large-7">
                    <ErrorBoundary>
                      <CgiSection
                        isFamilyFirstUS={modelInfo.isFamilyFirstUS}
                        country={modelInfo.country}
                        config={config}
                        attributes={attributes}
                        images={images}
                        isInteriorPrimary={isInteriorImagePrimary}
                        interiorIndex={interiorIndex}
                        exteriorIndex={exteriorIndex}
                        isConvertibleTopDown={isConvertibleTopDown}
                        onToggleConvertibleTop={onToggleConvertibleTop}
                        onToggleInterior={onToggleInterior}
                        onChangeExteriorIndex={onChangeExteriorIndex}
                        onChangeInteriorIndex={onChangeInteriorIndex}
                      />
                    </ErrorBoundary>
                  </div>
                  <div id="byo-detail-content" className="columns">
                    <div className="show-for-large">
                      <AccessoriesPrice value={pricing.accessoriesTotal} />
                    </div>
                    <ErrorBoundary>
                      <div className="sidebar-container">
                        <Switch>
                          <Route
                            path="/"
                            exact
                            render={() => (
                              <Redirect
                                to={
                                  modelInfo.enableConfigStep
                                    ? '/config'
                                    : '/trim'
                                }
                              />
                            )}
                          />
                          <Route
                            path="/config"
                            render={() => <Config features={trimFeatures} />}
                          />
                          <Route path="/trim" component={Trims} />
                          <Route path="/color" component={Colors} />
                          <Route path="/packages" component={Packages} />
                          <Route path="/exterior" component={Exterior} />
                          <Route path="/interior" component={Interior} />
                          <Route path="/accessories" component={Accessories} />
                          <Route path="/summary" component={Summary} />
                          <Route
                            render={() => (
                              <Redirect
                                to={
                                  modelInfo.enableConfigStep
                                    ? '/config'
                                    : '/trim'
                                }
                              />
                            )}
                          />
                        </Switch>
                      </div>
                    </ErrorBoundary>
                    <NavNextButton next={steps.next} />
                    <Footer
                      pathname={location.pathname}
                      sitemap={urls.sitemap}
                      appResources={appResources}
                      selections={config.selections}
                      serializedState={config.ss}
                      postalCode={params.postalCode}
                      uiPath={modelInfo.uiPath}
                      isFamilyFirst={modelInfo.isFamilyFirst}
                    />
                  </div>
                </div>
              </React.Fragment>
            )}
          </LayoutContext.Consumer>
        </LayoutManager>
        <ErrorBoundary>
          <StepNav steps={steps} />
          <Route path="/:parent/ads" component={AdChoices} />
          <Route
            path="/:pathname/zoom/:type/:index"
            component={ImageZoomModal}
          />
          <OptionConflict />
          <InstructionsModal
            disableTutorial={!modelInfo.enableTutorial}
            isOpen={isInstructionsOpen}
            onClose={() => {
              updateExpiration(new Date());
              this.setState({ isInstructionsOpen: false });
            }}
          />
          {isPostalCodeModalOpen &&
            !isInstructionsOpen && (
              <Route path="/summary">
                {({ match }) => (
                  <PostalCodeModal
                    initialValue={isDefaultPostalCode ? '' : params.postalCode}
                    onClose={closePostalCodeModal}
                    onUpdate={(postalCode, data, cb) => {
                      onPostalCodeUpdate(postalCode, data, !!match, cb);
                    }}
                  />
                )}
              </Route>
            )}
          <BreakpointChange onChange={onBreakpointChange} />
          <BackToTop />
          <OptionsLoadingOverlay isLoading={isUpdating} />
          <CookiesNotifications />
        </ErrorBoundary>
      </React.Fragment>
    );
  }
}

export default withRouter(
  withModelInfo(connect(mapStateToProps, mapDispatchToProps)(Main))
);
